import {
  MouseEvent,
  ReactElement,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useTransition,
} from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { DocumentationContentsMenu } from '../components';
import { DocumentationTokenProvider } from '../contexts/documentation-token';
import { SnippetScope } from '../contexts/snippet-scope';
import {
  DEFAULT_SCROLL_TARGET_OFFSET,
  SCROLL_TARGET_CLASS_NAME,
} from '../hooks/scroll';
import classNames from '../utilities/class-names';
import {
  findDocumentationContentsTitle,
} from '../utilities/documentation-contents';
import { Dashboard } from './Dashboard';
import './Documentation.scss';

// Matches SITE_NAME in scripts/prerender.config.js
const SITE_NAME = 'jsonpad';

// How long to keep looking for a hash target that hasn't rendered yet
const HASH_TARGET_ATTEMPTS = 20;
const HASH_TARGET_INTERVAL = 50;

// The page the browser actually loaded. Its <title> came from the pre-rendered
// HTML, so it's left alone rather than recomputed on the client.
const initialDocument = {
  pathname: normalisePathname(window.location.pathname),
  title: document.title,
};

function normalisePathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * Keep the tab title in step with client-side navigation between docs pages
 */
function updateDocumentTitle(
  pathname: string,
  content: HTMLElement | null
): void {
  const normalisedPathname = normalisePathname(pathname);

  if (normalisedPathname === initialDocument.pathname) {
    document.title = initialDocument.title;
    return;
  }

  const title =
    normalisedPathname === '/docs'
      ? 'Documentation'
      : findDocumentationContentsTitle(normalisedPathname) ??
        content?.querySelector('h1')?.textContent?.trim();

  if (title) {
    document.title = `${title} | ${SITE_NAME}`;
  }
}

function findHashTarget(name: string): Element | null {
  return (
    document.querySelector(
      `.${SCROLL_TARGET_CLASS_NAME}[data-name="${CSS.escape(name)}"]`
    ) ?? document.getElementById(name)
  );
}

type DocumentationProps = {
  className?: string;
  children?: ReactNode;
  [key: string]: any;
};

/**
 * A single documentation page's content. The surrounding chrome (header,
 * menus, contents) lives in DocumentationLayout, which stays mounted while
 * moving between pages.
 */
export function Documentation({
  className,
  children,
  ...props
}: DocumentationProps): ReactElement {
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    updateDocumentTitle(location.pathname, contentRef.current);
  }, [location.pathname]);

  return (
    <div
      ref={contentRef}
      className={classNames('documentation-content', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Route element for /docs/*
 *
 * Links in the contents menu and in page content stay as plain <a href>
 * elements, so the pre-rendered HTML and markdown twins are unaffected, but
 * clicks on them are turned into client-side navigations. Only the page
 * content re-renders, so the contents menu keeps its expanded sections and
 * scroll position.
 */
export function DocumentationLayout(): ReactElement {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  // Navigating in a transition keeps the current page on screen while the
  // next page's chunk loads, instead of blanking the content area
  const [navigating, startTransition] = useTransition();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = (event.target as Element).closest('a');
      if (
        !anchor ||
        !anchor.hasAttribute('href') ||
        anchor.hasAttribute('download') ||
        (anchor.target && anchor.target !== '_self') ||
        !anchor.closest('.documentation-contents-menu, .documentation-content')
      ) {
        return;
      }

      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin) {
        return;
      }

      // Paths with a file extension are real files (markdown twins, schemas,
      // downloads) rather than routes
      const lastSegment = url.pathname.slice(url.pathname.lastIndexOf('/'));
      if (lastSegment.includes('.')) {
        return;
      }

      event.preventDefault();

      const to = `${url.pathname}${url.search}${url.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      startTransition(() => {
        navigate(to, { replace: to === current });
      });
    },
    [navigate]
  );

  // Scroll to the top of a new page, or to its hash target. The initial page
  // load is left to the browser and useScroll, and back/forward navigation to
  // the browser's own scroll restoration.
  const previousLocation = useRef(location);
  useEffect(() => {
    const previous = previousLocation.current;
    previousLocation.current = location;

    if (previous.key === location.key || navigationType === 'POP') {
      return;
    }

    const pathnameChanged = previous.pathname !== location.pathname;
    const hash = decodeURIComponent(location.hash.replace(/^#/, ''));

    if (!hash) {
      if (pathnameChanged) {
        window.scrollTo({ top: 0, left: 0 });
      }
      return;
    }

    let attempts = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const scrollToHashTarget = () => {
      const target = findHashTarget(hash);

      if (target) {
        window.scrollTo({
          top:
            target.getBoundingClientRect().top +
            window.scrollY +
            DEFAULT_SCROLL_TARGET_OFFSET,
          behavior: pathnameChanged ? 'auto' : 'smooth',
        });
      } else if (attempts++ < HASH_TARGET_ATTEMPTS) {
        timeout = setTimeout(scrollToHashTarget, HASH_TARGET_INTERVAL);
      }
    };

    scrollToHashTarget();

    return () => clearTimeout(timeout);
  }, [location, navigationType]);

  return (
    <DocumentationTokenProvider>
      <Dashboard
        className={classNames(
          'documentation-layout',
          navigating ? 'documentation-layout-navigating' : undefined
        )}
        onClick={handleClick}
      >
        <DocumentationContentsMenu />

        {/* Deliberately not .documentation-content, so that the pre-renderer
        can tell a loaded page apart from the placeholder */}
        <Suspense
          fallback={<div className="documentation-content-loading" />}
        >
          {/* Each page's snippets share their own ids */}
          <SnippetScope resetKey={location.pathname}>
            <Outlet />
          </SnippetScope>
        </Suspense>
      </Dashboard>
    </DocumentationTokenProvider>
  );
}
